import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Tabs, Tab, Spinner, NonIdealState, Button, Tag, Icon, Callout,
  Card, InputGroup, TextArea, Tooltip,
} from "@blueprintjs/core";
import {
  api,
  type CompanyDetail as CompanyDetailType,
  type CompanyContact,
  type CompanyNote,
  type CompanyActivityItem,
} from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { LeadDetailDrawer } from "../components/LeadDetailDrawer";

const COMPANY_STATUSES = ["new", "researching", "engaged", "qualified", "won"] as const;
const TERMINAL_STATUSES = new Set(["won", "lost", "not_interested"]);

const STATUS_LABELS: Record<string, string> = {
  new: "New",
  researching: "Researching",
  engaged: "Engaged",
  qualified: "Qualified",
  won: "Won",
  lost: "Lost",
  not_interested: "Not Interested",
};

const STATUS_INTENT: Record<string, "success" | "primary" | "warning" | "danger" | "none"> = {
  new: "none",
  researching: "primary",
  engaged: "primary",
  qualified: "warning",
  won: "success",
  lost: "danger",
  not_interested: "danger",
};

const AVATAR_COLORS = ["#2b95d6", "#634dbf", "#0d8050", "#c87619", "#8f398f", "#1f6b6b", "#96290d", "#5642a6"];
function avatarColor(name: string) {
  let h = 0;
  for (const c of name) h = ((h << 5) - h + c.charCodeAt(0)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function errMsg(e: unknown): string {
  if (typeof e === "string") return e;
  if (e && typeof e === "object" && "message" in e) return String((e as { message: unknown }).message);
  return "Something went wrong";
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

function ContactStatusTag({ status }: { status: string }) {
  const map: Record<string, { intent: "success" | "primary" | "warning" | "danger" | "none"; label: string }> = {
    qualified: { intent: "success", label: "Qualified" },
    contacted: { intent: "primary", label: "Contacted" },
    unclaimed: { intent: "none", label: "Unclaimed" },
    claimed: { intent: "warning", label: "Claimed" },
    won: { intent: "success", label: "Won" },
    dead: { intent: "danger", label: "Dead" },
    lost: { intent: "danger", label: "Lost" },
    interested: { intent: "primary", label: "Interested" },
  };
  const cfg = map[status] ?? { intent: "none" as const, label: status };
  return <Tag minimal round intent={cfg.intent}>{cfg.label}</Tag>;
}

const VALID_TABS = new Set(["overview", "contacts", "activity"]);

export function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [company, setCompany] = useState<CompanyDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  const [activities, setActivities] = useState<CompanyActivityItem[]>([]);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  const tabParam = searchParams.get("tab") || "overview";
  const activeTab = VALID_TABS.has(tabParam) ? tabParam : "overview";
  const contactParam = searchParams.get("contact");
  const selectedLeadId = contactParam || null;
  const drawerOpen = !!contactParam;

  const setActiveTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    next.delete("contact");
    setSearchParams(next, { replace: true });
  };

  const openContactDrawer = (contactId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("contact", contactId);
    setSearchParams(next);
  };

  const closeContactDrawer = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("contact");
    setSearchParams(next, { replace: true });
  };

  const fetchCompany = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await api.get<CompanyDetailType>(`/companies/${id}`);
      setCompany(data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [id, refreshKey]);

  const fetchActivities = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<CompanyActivityItem[]>(`/companies/${id}/activities`);
      setActivities(data);
    } catch {}
  }, [id, refreshKey]);

  useEffect(() => { fetchCompany(); }, [fetchCompany]);
  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const updateStatus = async (newStatus: string) => {
    if (!company) return;
    try {
      setStatusUpdating(true);
      const updated = await api.patch<CompanyDetailType>(`/companies/${company.id}/status`, { status: newStatus });
      setCompany(updated);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setStatusUpdating(false);
    }
  };

  const addNote = async () => {
    if (!id || !newNote.trim()) return;
    try {
      setAddingNote(true);
      await api.post(`/companies/${id}/notes`, { content: newNote.trim() });
      setNewNote("");
      fetchActivities();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setAddingNote(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 80 }}>
        <Spinner size={36} />
      </div>
    );
  }

  if (error && !company) {
    return <NonIdealState icon="error" title="Failed to load" description={error} />;
  }

  if (!company) {
    return <NonIdealState icon="office" title="Company not found" />;
  }

  const currentIdx = COMPANY_STATUSES.indexOf(company.status as typeof COMPANY_STATUSES[number]);

  return (
    <div>
      {/* Inline Breadcrumbs */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#5c7080", marginBottom: 16 }}>
        {(() => {
          const bpId = searchParams.get("bp") || company.blueprint_id;
          const bpName = company.blueprint_name;
          if (bpId && bpName) {
            return (
              <>
                <span className="breadcrumb-link" onClick={() => navigate("/blueprints")}>Blueprints</span>
                <Icon icon="chevron-right" size={12} />
                <span className="breadcrumb-link" onClick={() => navigate(`/blueprints/${bpId}?tab=leads`)}>
                  {bpName}
                </span>
                <Icon icon="chevron-right" size={12} />
              </>
            );
          }
          return (
            <>
              <span className="breadcrumb-link" onClick={() => navigate("/leads")}>Companies</span>
              <Icon icon="chevron-right" size={12} />
            </>
          );
        })()}
        <span style={{ color: "#1c2127", fontWeight: 500 }}>{company.name}</span>
      </div>

      {error && <Callout intent="danger" style={{ marginBottom: 12 }}>{error}</Callout>}

      {/* Company Header */}
      <div className="company-header-card">
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
          <div
            className="company-avatar-lg"
            style={{ background: avatarColor(company.name) }}
          >
            {company.name.charAt(0).toUpperCase()}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{company.name}</h2>
              <Tag
                intent={STATUS_INTENT[company.status] || "none"}
                round
                large
                style={{ fontSize: 12, fontWeight: 600 }}
              >
                {STATUS_LABELS[company.status] || company.status}
              </Tag>
            </div>

            <div style={{ display: "flex", gap: 24, marginTop: 8, flexWrap: "wrap", fontSize: 13, color: "#5c7080" }}>
              {company.domain && (
                <Tooltip content="Open website">
                  <a href={`https://${company.domain}`} target="_blank" rel="noopener noreferrer" style={{ color: "#2b95d6", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
                    <Icon icon="globe" size={12} /> {company.domain}
                  </a>
                </Tooltip>
              )}
              {company.industry && (
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Icon icon="office" size={12} /> {company.industry}
                </span>
              )}
              {company.location && (
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Icon icon="map-marker" size={12} /> {company.location}
                </span>
              )}
              {company.employee_count && (
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Icon icon="people" size={12} /> {company.employee_count.toLocaleString()} employees
                </span>
              )}
              {company.phone && (
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Icon icon="phone" size={12} /> {company.phone}
                </span>
              )}
            </div>

            {/* Status Pipeline */}
            <div style={{ marginTop: 16 }}>
              <div className="company-pipeline">
                {COMPANY_STATUSES.map((s, i) => {
                  const isActive = company.status === s;
                  const isPast = currentIdx >= 0 && i < currentIdx;
                  const isTerminal = TERMINAL_STATUSES.has(company.status);
                  const isClickable = !isTerminal || s === company.status;

                  return (
                    <button
                      key={s}
                      className={`pipeline-stage ${isActive ? "active" : ""} ${isPast ? "past" : ""} ${!isClickable ? "disabled" : ""}`}
                      disabled={!isClickable || statusUpdating || isActive}
                      onClick={() => isClickable && !isActive && updateStatus(s)}
                    >
                      {isPast ? <Icon icon="tick" size={10} /> : null}
                      {STATUS_LABELS[s]}
                    </button>
                  );
                })}
              </div>

              {!TERMINAL_STATUSES.has(company.status) && (
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <Button
                    small
                    minimal
                    intent="danger"
                    icon="cross"
                    onClick={() => updateStatus("not_interested")}
                    disabled={statusUpdating}
                  >
                    Not Interested
                  </Button>
                  <Button
                    small
                    minimal
                    intent="danger"
                    icon="disable"
                    onClick={() => updateStatus("lost")}
                    disabled={statusUpdating}
                  >
                    Lost
                  </Button>
                </div>
              )}

              {TERMINAL_STATUSES.has(company.status) && company.status !== "won" && (
                <div style={{ marginTop: 8 }}>
                  <Button small minimal intent="primary" icon="undo" onClick={() => updateStatus("new")} disabled={statusUpdating}>
                    Reopen
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div style={{ display: "flex", gap: 12 }}>
            <div className="company-stat-box">
              <div className="company-stat-value">{company.contact_count}</div>
              <div className="company-stat-label">Contacts</div>
            </div>
            <div className="company-stat-box">
              <div className="company-stat-value">{Math.round(company.completeness_score * 100)}%</div>
              <div className="company-stat-label">Data Quality</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ marginTop: 20 }}>
      <Tabs
        id="company-tabs"
        selectedTabId={activeTab}
        onChange={(t) => setActiveTab(t as string)}
        renderActiveTabPanelOnly
      >
        <Tab id="overview" title="Overview" panel={
          <OverviewPanel company={company} />
        } />
        <Tab id="contacts" title={`Contacts (${company.contacts.length})`} panel={
          <ContactsPanel
            contacts={company.contacts}
            onContactClick={(cid) => openContactDrawer(cid)}
            onRefresh={() => setRefreshKey(k => k + 1)}
          />
        } />
        <Tab id="activity" title="Activity & Notes" panel={
          <ActivityPanel
            activities={activities}
            newNote={newNote}
            setNewNote={setNewNote}
            addingNote={addingNote}
            onAddNote={addNote}
          />
        } />
      </Tabs>
      </div>

      <LeadDetailDrawer
        leadId={selectedLeadId}
        isOpen={drawerOpen}
        onClose={closeContactDrawer}
        onLeadChanged={() => setRefreshKey(k => k + 1)}
      />
    </div>
  );
}

function OverviewPanel({ company }: { company: CompanyDetailType }) {
  const enrichment = company.enrichment_data || {};
  const hasEnrichment = Object.keys(enrichment).length > 0;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, paddingTop: 16 }}>
      {/* Left: Company Info */}
      <div>
        <div className="dash-panel">
          <div className="dash-panel-header" style={{ fontWeight: 600 }}>Company Information</div>
          <div className="dash-panel-body">
            <InfoRow label="Name" value={company.name} />
            <InfoRow label="Domain" value={company.domain} link={company.domain ? `https://${company.domain}` : undefined} />
            <InfoRow label="Industry" value={company.industry} />
            <InfoRow label="Category" value={company.category} />
            <InfoRow label="Location" value={company.location} />
            <InfoRow label="Country" value={company.country} />
            <InfoRow label="Address" value={company.address} />
            <InfoRow label="Phone" value={company.phone} copyable />
            <InfoRow label="Employees" value={company.employee_count?.toLocaleString()} />
            <InfoRow label="Website" value={company.website} link={company.website || undefined} />
            <InfoRow label="LinkedIn" value={company.linkedin_url ? "View Profile" : null} link={company.linkedin_url || undefined} />
            {company.rating != null && (
              <InfoRow label="Rating" value={`${company.rating}${company.rating ? ` (${company.rating} reviews)` : ""}`} />
            )}
          </div>
        </div>

        {company.description && (
          <div className="dash-panel" style={{ marginTop: 16 }}>
            <div className="dash-panel-header" style={{ fontWeight: 600 }}>Description</div>
            <div className="dash-panel-body">
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.65, color: "#394b59" }}>
                {company.description}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Right: Tech & Enrichment */}
      <div>
        {company.tech_stack && company.tech_stack.length > 0 && (
          <div className="dash-panel">
            <div className="dash-panel-header" style={{ fontWeight: 600 }}>Tech Stack</div>
            <div className="dash-panel-body">
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {company.tech_stack.map((t, i) => (
                  <Tag key={i} minimal round>{t}</Tag>
                ))}
              </div>
            </div>
          </div>
        )}

        {company.social_links && Object.keys(company.social_links).length > 0 && (
          <div className="dash-panel" style={{ marginTop: 16 }}>
            <div className="dash-panel-header" style={{ fontWeight: 600 }}>Social Links</div>
            <div className="dash-panel-body">
              {Object.entries(company.social_links).map(([platform, url]) => (
                <div key={platform} style={{ marginBottom: 6 }}>
                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: "#2b95d6", textDecoration: "none", textTransform: "capitalize" }}>
                    {platform}
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {hasEnrichment && (
          <div className="dash-panel" style={{ marginTop: 16 }}>
            <div className="dash-panel-header" style={{ fontWeight: 600 }}>Enrichment Data</div>
            <div className="dash-panel-body">
              {Object.entries(enrichment).map(([key, val]) => (
                <InfoRow key={key} label={key.replace(/_/g, " ")} value={typeof val === "object" ? JSON.stringify(val) : String(val ?? "—")} />
              ))}
            </div>
          </div>
        )}

        <div className="dash-panel" style={{ marginTop: 16 }}>
          <div className="dash-panel-header" style={{ fontWeight: 600 }}>Contact Summary</div>
          <div className="dash-panel-body">
            <InfoRow label="Total Contacts" value={String(company.contact_count)} />
            <InfoRow label="Claimed" value={String(company.contacts.filter(c => c.status !== "unclaimed").length)} />
            <InfoRow label="Qualified" value={String(company.contacts.filter(c => c.status === "qualified").length)} />
            <InfoRow label="Data Quality" value={`${Math.round(company.completeness_score * 100)}%`} />
            <InfoRow label="Discovered" value={fmtDate(company.created_at)} />
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, link, copyable }: { label: string; value: string | null | undefined; link?: string; copyable?: boolean }) {
  if (!value) return null;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copyToClipboard(value!);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={{ display: "flex", padding: "6px 0", borderBottom: "1px solid #f0f0f0", gap: 8 }}>
      <span style={{ width: 120, flexShrink: 0, fontSize: 12, fontWeight: 500, color: "#5c7080", textTransform: "capitalize" }}>{label}</span>
      <span style={{ fontSize: 13, color: "#1c2127", flex: 1 }}>
        {link ? (
          <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: "#2b95d6", textDecoration: "none" }}>{value}</a>
        ) : value}
      </span>
      {copyable && value && (
        <Tooltip content={copied ? "Copied!" : "Copy"}>
          <Icon
            icon={copied ? "tick" : "duplicate"}
            size={12}
            color={copied ? "#0d8050" : "#8a9ba8"}
            style={{ cursor: "pointer" }}
            onClick={handleCopy}
          />
        </Tooltip>
      )}
    </div>
  );
}

function RevealButton({ leadId, type, onRevealed }: { leadId: string; type: "email" | "phone"; onRevealed: () => void }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const handleReveal = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    setErr("");
    try {
      await api.post(`/leads/${leadId}/reveal-${type}`);
      onRevealed();
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip content={err || `Reveal ${type} (~${type === "email" ? "1" : "5-10"} credits)`} minimal intent={err ? "danger" : "none"}>
      <Button
        small
        minimal
        icon={type === "email" ? "envelope" : "phone"}
        intent="primary"
        loading={loading}
        onClick={handleReveal}
        style={{ fontSize: 11 }}
      >
        Reveal
      </Button>
    </Tooltip>
  );
}

function ContactsPanel({ contacts, onContactClick, onRefresh }: { contacts: CompanyContact[]; onContactClick: (id: string) => void; onRefresh: () => void }) {
  if (contacts.length === 0) {
    return <div style={{ padding: 40 }}><NonIdealState icon="people" title="No contacts" description="No contacts have been discovered for this company." /></div>;
  }

  return (
    <div style={{ paddingTop: 16 }}>
      <table className="lead-table" style={{ tableLayout: "fixed" }}>
        <thead>
          <tr>
            <th style={{ width: "22%" }}>Name</th>
            <th style={{ width: "17%" }}>Title</th>
            <th style={{ width: "20%" }}>Email</th>
            <th style={{ width: "16%" }}>Phone</th>
            <th style={{ width: "6%" }}>Score</th>
            <th style={{ width: "10%" }}>Status</th>
            <th style={{ width: "9%" }}>Claimed</th>
          </tr>
        </thead>
        <tbody>
          {contacts.map(c => (
            <tr
              key={c.id}
              onClick={() => onContactClick(c.id)}
              style={{ cursor: "pointer" }}
              className="contact-table-row"
            >
              <td>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                      background: avatarColor(`${c.first_name}${c.last_name}`),
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 700, color: "#fff",
                    }}
                  >
                    {c.first_name[0]}{c.last_name[0]}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{c.first_name} {c.last_name}</div>
                  </div>
                </div>
              </td>
              <td style={{ fontSize: 13, color: "#394b59" }}>{c.job_title || "—"}</td>
              <td>
                {c.email ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Icon icon="tick-circle" size={12} color="#0d8050" />
                    <span style={{ fontSize: 12.5, color: "#394b59", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email}</span>
                  </div>
                ) : (
                  <RevealButton leadId={c.id} type="email" onRevealed={onRefresh} />
                )}
              </td>
              <td>
                {c.phone ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Icon icon="tick-circle" size={12} color="#0d8050" />
                    <span style={{ fontSize: 12.5, color: "#394b59" }}>{c.phone}</span>
                  </div>
                ) : (
                  <RevealButton leadId={c.id} type="phone" onRevealed={onRefresh} />
                )}
              </td>
              <td>
                {c.score > 0 && (
                  <Tag minimal round intent={c.score >= 80 ? "success" : c.score >= 60 ? "warning" : "none"} style={{ fontSize: 11, fontWeight: 600 }}>
                    {c.score}
                  </Tag>
                )}
              </td>
              <td><ContactStatusTag status={c.status} /></td>
              <td style={{ fontSize: 12, color: "#5c7080" }}>{c.claimer_name || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ACTIVITY_ICON: Record<string, string> = {
  claimed: "hand",
  released: "hand-up",
  status_changed: "swap-horizontal",
  follow_up_created: "calendar",
  follow_up_completed: "tick-circle",
  note_added: "annotation",
  company_status_changed: "swap-horizontal",
  company_auto_status: "automatic-updates",
  note: "annotation",
};

const ACTIVITY_INTENT: Record<string, "success" | "primary" | "warning" | "danger" | "none"> = {
  claimed: "primary",
  released: "warning",
  status_changed: "primary",
  follow_up_created: "none",
  follow_up_completed: "success",
  note_added: "none",
  company_status_changed: "warning",
  company_auto_status: "success",
  note: "none",
};

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ActivityPanel({
  activities,
  newNote,
  setNewNote,
  addingNote,
  onAddNote,
}: {
  activities: CompanyActivityItem[];
  newNote: string;
  setNewNote: (v: string) => void;
  addingNote: boolean;
  onAddNote: () => void;
}) {
  return (
    <div style={{ paddingTop: 16, maxWidth: 740 }}>
      {/* Add note */}
      <div className="dash-panel" style={{ marginBottom: 24 }}>
        <div className="dash-panel-header" style={{ fontWeight: 600 }}>Add Note</div>
        <div className="dash-panel-body">
          <TextArea
            fill
            rows={3}
            placeholder="Write a note about this company..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <Button
            small
            intent="primary"
            icon="annotation"
            onClick={onAddNote}
            disabled={addingNote || !newNote.trim()}
            loading={addingNote}
          >
            Add Note
          </Button>
        </div>
      </div>

      {/* Unified timeline */}
      {activities.length === 0 ? (
        <div style={{ padding: 40 }}><NonIdealState icon="history" title="No activity yet" description="Activities from contacts and company notes will appear here." /></div>
      ) : (
        <div className="company-activity-timeline">
          {activities.map(item => {
            const isNote = item.type === "note";
            const icon = ACTIVITY_ICON[item.action] || "circle";
            const intent = ACTIVITY_INTENT[item.action] || "none";
            const intentColor = intent === "success" ? "#0d8050"
              : intent === "primary" ? "#2b95d6"
              : intent === "warning" ? "#c87619"
              : intent === "danger" ? "#db3737"
              : "#8a9ba8";

            return (
              <div key={item.id} className="activity-timeline-item">
                <div className="activity-timeline-marker" style={{ borderColor: intentColor }}>
                  <Icon icon={icon as any} size={12} color={intentColor} />
                </div>
                <div className="activity-timeline-content">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: "#1c2127" }}>
                        {item.actor_name || "System"}
                      </span>
                      {!isNote && (
                        <span style={{ fontSize: 13, color: "#5c7080", marginLeft: 6 }}>
                          {item.content}
                        </span>
                      )}
                      {isNote && item.entity_type === "lead" && (
                        <Tag minimal round style={{ marginLeft: 6, fontSize: 10 }}>
                          {item.entity_name ? `Note on ${item.entity_name}` : "Contact note"}
                        </Tag>
                      )}
                    </div>
                    <span style={{ fontSize: 11, color: "#8a9ba8", whiteSpace: "nowrap", flexShrink: 0 }}>
                      {fmtDateTime(item.created_at)}
                    </span>
                  </div>
                  {isNote && item.content && (
                    <p style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.6, color: "#394b59", whiteSpace: "pre-wrap" }}>
                      {item.content}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
